/**
 * Authentication utility functions
 */

import { msalInstance } from './AuthWrapper';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

export interface UserInfo {
  name?: string;
  username?: string;
  email?: string;
}

/**
 * Get an access token for the specified scopes
 * @param scopes - Array of scopes to request
 * @returns Access token or null
 */
export const getAccessToken = async (scopes: string[]): Promise<string | null> => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    console.error('No active account found');
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes, account });
      return null;
    }
    console.error('Failed to acquire token silently:', error);
    return null;
  }
};

/**
 * Get current user information from active account
 * @returns User info object or null
 */
export const getUserInfo = (): UserInfo | null => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;

  return {
    name: account.name || undefined,
    username: account.username || undefined,
    email: account.username || undefined,
  };
};

/**
 * Get an access token silently only (no interactive redirect).
 * Use for non-critical features like avatar loading where a redirect would be disruptive.
 */
export const getAccessTokenSilent = async (scopes: string[]): Promise<string | null> => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({ scopes, account });
    return response.accessToken;
  } catch {
    return null;
  }
};

/**
 * Sign out the current user
 */
export const logout = async (): Promise<void> => {
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: window.location.origin + '/',
  });
};
